import { useState, useRef, useCallback, useEffect, forwardRef, useImperativeHandle } from "react"

const MIN_SEL = 20

const HANDLES = [
  { id: "nw", style: { top: -6, left: -6 },                         cursor: "nw-resize" },
  { id: "n",  style: { top: -6, left: "calc(50% - 5px)" },         cursor: "n-resize"  },
  { id: "ne", style: { top: -6, right: -6 },                        cursor: "ne-resize" },
  { id: "e",  style: { top: "calc(50% - 5px)", right: -6 },        cursor: "e-resize"  },
  { id: "se", style: { bottom: -6, right: -6 },                     cursor: "se-resize" },
  { id: "s",  style: { bottom: -6, left: "calc(50% - 5px)" },      cursor: "s-resize"  },
  { id: "sw", style: { bottom: -6, left: -6 },                      cursor: "sw-resize" },
  { id: "w",  style: { top: "calc(50% - 5px)", left: -6 },         cursor: "w-resize"  },
]

const clamp = (v, lo, hi) => Math.max(lo, Math.min(hi, v))

/**
 * Free-aspect crop editor.
 * ref.current.crop() → Promise<Blob> (image/webp)
 */
const CropEditor = forwardRef(function CropEditor({ src, height = 360, className = "" }, ref) {
  const containerRef = useRef(null)
  const imgRef = useRef(null)
  const [imgBounds, setImgBounds] = useState(null)
  const [sel, setSel] = useState(null)
  const dragRef = useRef(null)

  const measureImg = useCallback(() => {
    const img = imgRef.current
    const cont = containerRef.current
    if (!img || !cont || !img.naturalWidth) return
    const iR = img.getBoundingClientRect()
    const cR = cont.getBoundingClientRect()
    const b = { x: iR.left - cR.left, y: iR.top - cR.top, w: iR.width, h: iR.height }
    setImgBounds(b)
    setSel({ ...b })
  }, [])

  useEffect(() => {
    window.addEventListener("resize", measureImg)
    return () => window.removeEventListener("resize", measureImg)
  }, [measureImg])

  useImperativeHandle(ref, () => ({
    isCropped: () => {
      if (!sel || !imgBounds) return false
      const EPS = 2
      return (
        Math.abs(sel.x - imgBounds.x) > EPS ||
        Math.abs(sel.y - imgBounds.y) > EPS ||
        Math.abs(sel.w - imgBounds.w) > EPS ||
        Math.abs(sel.h - imgBounds.h) > EPS
      )
    },
    crop: (mimeType = "image/webp") => new Promise((resolve, reject) => {
      const img = imgRef.current
      if (!img || !imgBounds || !sel) return reject(new Error("No image"))
      const scaleX = img.naturalWidth / imgBounds.w
      const scaleY = img.naturalHeight / imgBounds.h
      const sx = Math.round((sel.x - imgBounds.x) * scaleX)
      const sy = Math.round((sel.y - imgBounds.y) * scaleY)
      const sw = Math.max(1, Math.round(sel.w * scaleX))
      const sh = Math.max(1, Math.round(sel.h * scaleY))
      const canvas = document.createElement("canvas")
      canvas.width = sw
      canvas.height = sh
      try {
        canvas.getContext("2d").drawImage(img, sx, sy, sw, sh, 0, 0, sw, sh)
        canvas.toBlob(resolve, mimeType, 0.9)
      } catch (err) {
        reject(err)
      }
    }),
  }))

  const toLocal = (e) => {
    const cR = containerRef.current.getBoundingClientRect()
    return { x: e.clientX - cR.left, y: e.clientY - cR.top }
  }

  const startDrag = useCallback((e, handle) => {
    e.preventDefault()
    e.stopPropagation()
    dragRef.current = { handle, start: toLocal(e), orig: { ...sel } }
    e.currentTarget.setPointerCapture(e.pointerId)
  }, [sel])

  const onPointerMove = useCallback((e) => {
    const d = dragRef.current
    if (!d || !imgBounds) return
    const pos = toLocal(e)
    const dx = pos.x - d.start.x
    const dy = pos.y - d.start.y
    let { x, y, w, h } = d.orig

    if (d.handle === "move") {
      setSel({
        x: clamp(x + dx, imgBounds.x, imgBounds.x + imgBounds.w - w),
        y: clamp(y + dy, imgBounds.y, imgBounds.y + imgBounds.h - h),
        w, h,
      })
      return
    }

    if (d.handle.includes("e")) w = clamp(w + dx, MIN_SEL, imgBounds.x + imgBounds.w - x)
    if (d.handle.includes("s")) h = clamp(h + dy, MIN_SEL, imgBounds.y + imgBounds.h - y)
    if (d.handle.includes("w")) {
      const nx = clamp(x + dx, imgBounds.x, x + w - MIN_SEL)
      w = w + x - nx; x = nx
    }
    if (d.handle.includes("n")) {
      const ny = clamp(y + dy, imgBounds.y, y + h - MIN_SEL)
      h = h + y - ny; y = ny
    }
    setSel({ x, y, w, h })
  }, [imgBounds])

  return (
    <div
      ref={containerRef}
      className={`relative bg-neutral-900 select-none rounded-xl overflow-hidden w-full ${className}`}
      style={height ? { height } : { flex: 1 }}
      onPointerMove={onPointerMove}
      onPointerUp={() => { dragRef.current = null }}
      onPointerLeave={() => { dragRef.current = null }}
    >
      <img
        ref={imgRef}
        src={src}
        onLoad={measureImg}
        crossOrigin={src?.startsWith("data:") ? undefined : "anonymous"}
        draggable={false}
        alt=""
        className="max-w-full max-h-full absolute inset-0 m-auto block pointer-events-none select-none"
      />
      {sel && (
        <div
          className="absolute border border-white cursor-move"
          style={{
            left: sel.x, top: sel.y,
            width: sel.w, height: sel.h,
            boxShadow: "0 0 0 9999px rgba(0,0,0,0.6)",
            zIndex: 1,
            touchAction: "none",
          }}
          onPointerDown={(e) => startDrag(e, "move")}
        >
          {/* Rule-of-thirds grid */}
          <div className="absolute inset-0 pointer-events-none">
            {[33.33, 66.67].map((p) => (
              <div key={`v${p}`} className="absolute top-0 bottom-0 border-l border-white/20" style={{ left: `${p}%` }} />
            ))}
            {[33.33, 66.67].map((p) => (
              <div key={`h${p}`} className="absolute left-0 right-0 border-t border-white/20" style={{ top: `${p}%` }} />
            ))}
          </div>
          {/* Handles */}
          {HANDLES.map(({ id, style, cursor }) => (
            <div
              key={id}
              className="absolute w-3 h-3 bg-white rounded-sm shadow"
              style={{ ...style, cursor, position: "absolute", touchAction: "none" }}
              onPointerDown={(e) => startDrag(e, id)}
            />
          ))}
        </div>
      )}
    </div>
  )
})

export default CropEditor
